import React, { useState, useEffect } from 'react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  GripVertical, Clock, Zap, Play, User, Copy, Edit, Trash2, Archive, ArchiveRestore, Calculator
} from 'lucide-react';

// Pequeno componente que mostra hora de início + tempo decorrido a contar em tempo real
const LiveElapsed = ({ iniciadoEm, congeladoEm, tempoAcumuladoSeg = 0, ultimoDescongeladoEm }) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (congeladoEm) return; // não actualiza enquanto congelado
    const i = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(i);
  }, [congeladoEm, iniciadoEm, ultimoDescongeladoEm]);
  if (!iniciadoEm) return null;
  const inicioRef = ultimoDescongeladoEm || iniciadoEm;
  let totalSeg = Number(tempoAcumuladoSeg || 0);
  if (!congeladoEm) {
    try { totalSeg += Math.max(0, Math.floor((Date.now() - new Date(inicioRef).getTime()) / 1000)); }
    catch (_) { /* ignore */ }
  }
  const h = Math.floor(totalSeg / 3600);
  const m = Math.floor((totalSeg % 3600) / 60);
  const s = totalSeg % 60;
  const fmt = h > 0 ? `${h}h ${m}m ${String(s).padStart(2, '0')}s` : `${m}m ${String(s).padStart(2, '0')}s`;
  let horaInicio = '—';
  try { horaInicio = new Date(iniciadoEm).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' }); }
  catch (_) { /* ignore */ }
  return (
    <div className="flex items-center gap-3 text-[11px]">
      <span className="flex items-center gap-1 text-stone-600">
        <Clock className="h-3 w-3" />Início <strong className="text-stone-800">{horaInicio}</strong>
      </span>
      <span className="flex items-center gap-1">
        <span className={`inline-block w-1.5 h-1.5 rounded-full ${congeladoEm ? 'bg-amber-500' : 'bg-emerald-500 animate-pulse'}`} />
        <strong className={congeladoEm ? 'text-amber-700' : 'text-emerald-700'}>{fmt}</strong>
        {congeladoEm && <span className="text-amber-600">(congelada)</span>}
      </span>
    </div>
  );
};

export function TarefaCard({ tarefa, categoriasTarefas, draggedTarefa, onDragStart, onDragEnd, onDuplicate, onEdit, onArchive, onUnarchive, onDelete }) {
  const catInfo = tarefa.categoria_info || categoriasTarefas.find(c => c.id === tarefa.categoria_id);
  const dataCriacao = tarefa.criado_em
    ? new Date(tarefa.criado_em).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: '2-digit' })
    : null;

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, tarefa)}
      onDragEnd={onDragEnd}
      className={`bg-white rounded-lg border p-2.5 cursor-move transition-all hover:shadow-md ${
        draggedTarefa?.id === tarefa.id ? 'opacity-50 scale-95' : ''
      } ${tarefa.urgente ? 'border-l-4 border-l-red-500' : 'border-stone-200'}`}
      data-testid={`tarefa-card-${tarefa.id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-1.5 min-w-0 flex-1">
          <GripVertical className="h-3.5 w-3.5 text-stone-300 flex-shrink-0" />
          <h4 className="font-semibold text-sm text-navy truncate">{tarefa.nome}</h4>
        </div>
        {catInfo && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0" style={{ backgroundColor: `${catInfo.cor}20`, color: catInfo.cor }}>
            {catInfo.nome}
          </span>
        )}
      </div>

      {/* Linha de badges (Urgente + CONT + outros). Coexistem sempre — wrap se necessário. */}
      {(tarefa.urgente || tarefa.obriga_contagem || tarefa.is_contagem || (tarefa.operacoes || []).some(o => o?.is_contagem === true)) && (
        <div className="flex flex-wrap items-center gap-1 mb-1.5" data-testid={`tarefa-badges-${tarefa.id}`}>
          {tarefa.urgente && (
            <Badge variant="destructive" className="text-[10px] px-1.5 py-0" data-testid={`badge-urgente-${tarefa.id}`}>
              <Zap className="h-3 w-3 mr-0.5" />Urgente
            </Badge>
          )}
          {(tarefa.obriga_contagem || tarefa.is_contagem || (tarefa.operacoes || []).some(o => o?.is_contagem === true)) && (
            <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded font-bold bg-blue-100 text-blue-700" title="Tarefa com contagem" data-testid={`badge-cont-${tarefa.id}`}>
              <Calculator className="h-2.5 w-2.5" />CONT
            </span>
          )}
        </div>
      )}

      {(tarefa.operacoes || []).length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1.5">
          {(tarefa.operacoes || []).map((op, idx) => (
            <div key={idx} className="text-sm flex items-center gap-1 bg-primary/5 rounded px-1.5 py-0.5">
              <span className="font-mono font-bold text-primary">{op.operacao_codigo}</span>
              <span className="text-stone-600 truncate max-w-[140px]">{op.operacao_nome}</span>
            </div>
          ))}
        </div>
      )}

      {tarefa.observacoes && (
        <div className="text-sm text-stone-500 italic bg-stone-50 rounded px-1.5 py-0.5 mb-1.5 line-clamp-1">
          "{tarefa.observacoes}"
        </div>
      )}

      {tarefa.operadores_info && tarefa.operadores_info.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1.5">
          {tarefa.operadores_info.map((op, idx) => (
            <span key={idx} className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 rounded-full px-2 py-0.5">
              <User className="h-3 w-3" />{op.nome || op.username}
            </span>
          ))}
        </div>
      )}

      {tarefa.estado === 'producao' && tarefa.operador_info && (
        <div className="flex flex-col gap-1 bg-green-50 text-green-700 rounded px-2 py-1.5 mb-1.5 border border-green-200" data-testid={`tarefa-em-curso-${tarefa.id}`}>
          <div className="flex items-center gap-1 text-sm">
            <Play className="h-3 w-3 fill-current" />
            <span className="font-medium">Iniciado por: {tarefa.operador_info.nome || tarefa.operador_info.username}</span>
          </div>
          {tarefa.iniciado_em && (
            <LiveElapsed
              iniciadoEm={tarefa.iniciado_em}
              congeladoEm={tarefa.congelado_em}
              tempoAcumuladoSeg={tarefa.tempo_acumulado_segundos}
              ultimoDescongeladoEm={tarefa.ultimo_descongelado_em}
            />
          )}
        </div>
      )}

      {tarefa.operadores_ids_originais && tarefa.operadores_ids_originais.length > 1 && (
        <div className="text-[10px] text-stone-400 mb-1">
          Atribuida a {tarefa.operadores_ids_originais.length} operadores
        </div>
      )}

      <div className="flex items-center justify-between pt-1.5 border-t border-stone-100">
        {dataCriacao && (
          <span className="text-xs text-stone-400 flex items-center gap-0.5">
            <Clock className="h-3 w-3" />{dataCriacao}
          </span>
        )}
        <div className="flex gap-0.5 ml-auto">
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-purple-500 hover:text-purple-700 hover:bg-purple-50"
            onClick={(e) => { e.stopPropagation(); onDuplicate(tarefa); }} title="Duplicar tarefa">
            <Copy className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0"
            onClick={(e) => { e.stopPropagation(); onEdit(tarefa); }} title="Editar">
            <Edit className="h-3.5 w-3.5" />
          </Button>
          {tarefa.estado !== 'arquivado' && (
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-stone-400 hover:text-stone-600"
              onClick={(e) => { e.stopPropagation(); onArchive(tarefa.id); }} title="Arquivar">
              <Archive className="h-3.5 w-3.5" />
            </Button>
          )}
          {tarefa.estado === 'arquivado' && (
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-blue-500 hover:text-blue-700 hover:bg-blue-50"
              onClick={(e) => { e.stopPropagation(); onUnarchive(tarefa.id); }} title="Desarquivar"
              data-testid={`desarquivar-${tarefa.id}`}>
              <ArchiveRestore className="h-3.5 w-3.5" />
            </Button>
          )}
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
            onClick={(e) => { e.stopPropagation(); onDelete(tarefa.id); }} title="Eliminar">
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
